import { useState, useEffect, useCallback } from 'react';
import {
  getFileUploadPublicSettings,
  FileUploadSettingsPublicView,
} from '../api/General/fileUploadSettings';

/** Feature keys that map to FileUploadSettingsPublicView properties */
export type FileUploadFeatureKey = keyof FileUploadSettingsPublicView;

export interface FileSizeValidation {
  valid: boolean;
  maxMB: number;
}

// ── Module-level cache (shared across all hook instances) ──────────
let cachedSettings: FileUploadSettingsPublicView | null = null;
let fetchPromise: Promise<FileUploadSettingsPublicView | null> | null = null;

async function ensureSettings(): Promise<FileUploadSettingsPublicView | null> {
  if (cachedSettings) return cachedSettings;
  if (!fetchPromise) {
    fetchPromise = getFileUploadPublicSettings()
      .then((result) => {
        if (result.errors.length === 0) {
          cachedSettings = result.payload;
          return result.payload;
        }
        fetchPromise = null; // allow retry on error
        return null;
      })
      .catch(() => {
        fetchPromise = null;
        return null;
      });
  }
  return fetchPromise;
}

// ── Standalone utility (for class components / non-hook contexts) ──
/**
 * Validate a file's size against the configured limit for a given feature.
 * Returns { valid, maxMB }. If settings haven't loaded yet, allows the file
 * (backend will still enforce the limit).
 */
export async function validateFileSizeAsync(
  file: File,
  featureKey: FileUploadFeatureKey,
): Promise<FileSizeValidation> {
  const settings = await ensureSettings();
  if (!settings) return { valid: true, maxMB: 0 };
  const maxMB = settings[featureKey];
  return { valid: file.size <= maxMB * 1024 * 1024, maxMB };
}

/**
 * Get the max file size in MB for a feature. Returns the configured value
 * or a fallback if settings haven't loaded.
 */
export async function getMaxFileSizeMBAsync(
  featureKey: FileUploadFeatureKey,
  fallback = 10,
): Promise<number> {
  const settings = await ensureSettings();
  return settings?.[featureKey] ?? fallback;
}

// ── React Hook ─────────────────────────────────────────────────────
export function useFileUploadLimits() {
  const [settings, setSettings] = useState<FileUploadSettingsPublicView | null>(
    cachedSettings,
  );

  useEffect(() => {
    if (cachedSettings) {
      setSettings(cachedSettings);
      return;
    }
    ensureSettings().then((s) => {
      if (s) setSettings(s);
    });
  }, []);

  /** Synchronous validation (returns { valid: true } if settings not loaded yet) */
  const validateFileSize = useCallback(
    (file: File, featureKey: FileUploadFeatureKey): FileSizeValidation => {
      if (!settings) return { valid: true, maxMB: 0 };
      const maxMB = settings[featureKey];
      return { valid: file.size <= maxMB * 1024 * 1024, maxMB };
    },
    [settings],
  );

  /** Get max size in MB for a feature (0 if settings not loaded) */
  const getMaxSizeMB = useCallback(
    (featureKey: FileUploadFeatureKey): number => {
      return settings?.[featureKey] ?? 0;
    },
    [settings],
  );

  return { settings, validateFileSize, getMaxSizeMB };
}
